import { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { Helmet } from 'react-helmet';
import { Button, Form, Modal, Skeleton, Switch } from 'antd';

import { useBreadcrumbs } from '@/contexts/BreadcrumbContextProvider';

import InsurerGeneralForm from '@/components/Insurers/InsurerGeneralForm';
import { DefaultRouteTabs } from '@/components/_common';
import { SaveButton } from '@/components/_common/Buttons';
import {
  useDeleteInsurer,
  useInsurer,
} from '@/core/domain/people/Insurers/Insurers.hooks';
import { PageProps } from '@/types/PageProps';
import { InsurerFormValues } from '@/core/domain/people/Insurers/Insurers.types';
import { useInsurerFormContext } from '@/components/Insurers/InsurerFormProvider';
import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import Authorities from '@/types/Authority';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import InsurerFooter from '@/components/Insurers/InsurerFooter';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const InsurerGeneralPage = ({ route }: PageProps) => {
  const {
    isLoading,
    forms,
    insurerId,
    disabled,
    active,
    setActive,
    activateMutation,
    inactivateMutation,
    handleToggleActive,
  } = useInsurerFormContext();

  const intl = useIntl();
  const history = useHistory();
  const [form] = Form.useForm<InsurerFormValues>();

  const { isLoading: insurerLoadingDetail, data } = useInsurer(
    Number(insurerId),
  );

  const removeMutation = useDeleteInsurer();

  const { setParamLabel } = useBreadcrumbs();

  useEffect(() => {
    if (forms.insurerGeneral) {
      form.setFieldsValue({
        ...forms.insurerGeneral,
      });
    } else if (!isEmpty(data) && data) {
      form.setFieldsValue({
        name: data.name,
        fantasyName: data.fantasyName,
        cnpj: applyMask(data.cnpj, cnpjMask),
        email: data.email,
        stateRegistration: data.stateRegistration,
        cityRegistration: data.cityRegistration,
        refactoringMargin: data.refactoringMargin ? data.refactoringMargin : 0,
        website: data.website,
        observation: data.observation,
        retentionIss: data.retentionIss,
        michelinSegmentCode: data.michelinSegmentCode,
      });
      setParamLabel(':insurerId', data.name);

      setActive(data.active);
    }
  }, [insurerLoadingDetail, data, forms.insurerGeneral]);

  const handleSaveClick = async () => {
    try {
      await form.validateFields();
      form.submit();
    } catch {}
  };

  const handleRemoveClick = () => {
    Modal.confirm({
      title: intl.formatMessage({ id: 'insurers.remove.modal.title' }),
      content: intl.formatMessage({
        id: 'insurers.remove.modal.content',
      }),
      onOk: async () => {
        try {
          await removeMutation.mutateAsync(Number(insurerId));

          history.push('/app/registry/insurers');
        } catch {}
      },

      okText: intl.formatMessage({
        id: 'insurers.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  };

  return (
    <>
      <DefaultRouteTabs
        permissions={route.permissions}
        route={route.parent}
        baseKey={insurerId?.toString()}
        basePath={`/app/registry/insurers/${insurerId}`}
        extra={
          <SpaceForButton>
            <Switch
              disabled={
                !hasPermissions([Authorities.REGISTER_INSURER_ACTIVE_DEACTIVE])
              }
              checkedChildren={intl.formatMessage({
                id: 'insurers.filters.active.options.active',
              })}
              unCheckedChildren={intl.formatMessage({
                id: 'insurers.filters.active.options.inactive',
              })}
              loading={
                insurerLoadingDetail ||
                activateMutation.isLoading ||
                inactivateMutation.isLoading
              }
              checked={active}
              onChange={handleToggleActive}
            />

            <Button
              block
              type="ghost"
              danger
              onClick={handleRemoveClick}
              loading={insurerLoadingDetail}
              disabled={!hasPermissions([Authorities.REGISTER_INSURER_DELETE])}
            >
              {intl.formatMessage({ id: 'commons.entity.remove' })}
            </Button>

            <SaveButton
              block
              type="primary"
              onClick={handleSaveClick}
              loading={isLoading}
              disabled={
                disabled || !hasPermissions([Authorities.REGISTER_INSURER_EDIT])
              }
            >
              {intl.formatMessage({
                id: 'layout.menu.registry.insurers.saveText',
              })}
            </SaveButton>
          </SpaceForButton>
        }
      >
        <Helmet
          title={intl.formatMessage({
            id: route.name,
          })}
        />

        <Skeleton loading={insurerLoadingDetail}>
          <InsurerGeneralForm isEdit form={form} />

          <InsurerFooter disabled={disabled} />
        </Skeleton>
      </DefaultRouteTabs>
    </>
  );
};

export default InsurerGeneralPage;
